import Phaser from 'phaser';

import { CursorGuideController } from './CursorGuideController';

type CursorConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	x: number;
	y: number;
	boundsWidth: number;
	boundsHeight: number;
	onTraceStart: (x: number, y: number) => void;
	onTrace: (fromX: number, fromY: number, toX: number, toY: number) => CursorDragResult;
	onTraceEnd: () => void;
};

export type CursorDragResult = {
	suspendUntilRelease?: boolean;
	x: number;
	y: number;
};

const IDLE_SCALE = 1.08;
const IDLE_PULSE_DURATION = 1000;
const CURSOR_RADIUS = 64.5;
const CURSOR_STROKE_WIDTH = 8;
const CURSOR_STROKE_COLOR = 0xffffff;
const CURSOR_STROKE_ALPHA = 0.7;

export class Cursor {
	private readonly circle: Phaser.GameObjects.Arc;
	private readonly parent: Phaser.GameObjects.Container;
	private readonly scene: Phaser.Scene;
	private readonly boundsWidth: number;
	private readonly boundsHeight: number;
	private readonly onTraceStart: CursorConfig['onTraceStart'];
	private readonly onTrace: CursorConfig['onTrace'];
	private readonly onTraceEnd: CursorConfig['onTraceEnd'];
	private readonly pulseTween: Phaser.Tweens.Tween;
	private readonly guide: CursorGuideController;
	private enabled = true;
	private suspendedUntilRelease = false;

	constructor({ scene, parent, x, y, boundsWidth, boundsHeight, onTraceStart, onTrace, onTraceEnd }: CursorConfig) {
		this.scene = scene;
		this.parent = parent;
		this.boundsWidth = boundsWidth;
		this.boundsHeight = boundsHeight;
		this.onTraceStart = onTraceStart;
		this.onTrace = onTrace;
		this.onTraceEnd = onTraceEnd;
		this.circle = scene.add
			.circle(x, y, CURSOR_RADIUS, CURSOR_STROKE_COLOR, 0)
			.setStrokeStyle(CURSOR_STROKE_WIDTH, CURSOR_STROKE_COLOR, CURSOR_STROKE_ALPHA)
			.setInteractive({ useHandCursor: true });
		parent.add(this.circle);
		scene.input.setDraggable(this.circle);
		this.guide = new CursorGuideController({ scene, parent, cursor: this.circle });

		this.circle.on(Phaser.Input.Events.GAMEOBJECT_DRAG_START, this.startDragging, this);
		this.circle.on(Phaser.Input.Events.GAMEOBJECT_DRAG, this.drag, this);
		this.circle.on(Phaser.Input.Events.GAMEOBJECT_DRAG_END, this.stopDragging, this);

		this.pulseTween = scene.tweens.add({
			targets: this.circle,
			scale: { from: 1, to: IDLE_SCALE },
			duration: IDLE_PULSE_DURATION,
			ease: Phaser.Math.Easing.Sine.InOut,
			yoyo: true,
			repeat: -1,
		});
	}

	setPosition(x: number, y: number) {
		this.guide.hide();
		this.circle.setPosition(x, y);
	}

	setEnabled(enabled: boolean) {
		this.enabled = enabled;
		this.guide.setEnabled(enabled);
		this.suspendedUntilRelease = !enabled || this.scene.input.activePointer.isDown;

		if (enabled) {
			this.circle.setInteractive({ useHandCursor: true });
			this.scene.input.setDraggable(this.circle);
			this.pulseTween.play();
			return;
		}

		this.circle.disableInteractive();
		this.pulseTween.pause().seek(0);
	}

	private startDragging(pointer: Phaser.Input.Pointer) {
		if (!this.enabled) {
			return;
		}

		this.suspendedUntilRelease = false;
		this.pulseTween.pause().seek(0);
		const localPointer = this.getClampedLocalPointer(pointer);
		this.onTraceStart(localPointer.x, localPointer.y);
		this.drag(pointer);
	}

	private drag(pointer: Phaser.Input.Pointer) {
		if (!this.enabled || this.suspendedUntilRelease) {
			return;
		}

		const localPointer = this.getClampedLocalPointer(pointer);
		const result = this.onTrace(this.circle.x, this.circle.y, localPointer.x, localPointer.y);

		this.circle.setPosition(result.x, result.y);
		this.suspendedUntilRelease = result.suspendUntilRelease ?? false;
	}

	private stopDragging() {
		this.suspendedUntilRelease = false;
		this.onTraceEnd();

		if (this.enabled) {
			this.pulseTween.play();
		}
	}

	private getClampedLocalPointer(pointer: Phaser.Input.Pointer) {
		const localPointer = this.parent
			.getWorldTransformMatrix()
			.applyInverse(pointer.worldX, pointer.worldY, new Phaser.Math.Vector2());
		localPointer.x = Phaser.Math.Clamp(localPointer.x, 0, this.boundsWidth);
		localPointer.y = Phaser.Math.Clamp(localPointer.y, 0, this.boundsHeight);
		return localPointer;
	}
}
